export const getTenantNameFromAddressbar = (url: string): string | undefined => {
  try {
    const { hostname } = new URL(url);
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return parts[0];
    }
    return undefined;
  } catch (error) {
    console.error('Invalid URL:', error);
    return undefined;
  }
};

export const getTenantStorageUrl = (tenantName?: string): string => {
  return (tenantName != null) ? `https://storage.${tenantName}.jizaipad.com` : '';
};

/**
 * 再設定メールのリンク `/new-password?email=<email>` から email を取り出す。
 *
 * メール側(authentication_mailer#forgot_password)は email を URL エンコードせず
 * そのまま連結するため、`taro+test@example.com` のような '+' を含むアドレスが
 * 素のまま query に入る。ここで `URLSearchParams` / `searchParams.get` を使うと
 * application/x-www-form-urlencoded 規則で '+' が半角スペースへ変換され、
 * Cognito 上の username と一致せずパスワード再設定が失敗する。
 * そこで生のクエリ文字列から値を切り出し、'+' を保持する `decodeURIComponent` で復元する。
 *
 * @param search `location.search`(例: `?email=taro+test%40example.com`)
 * @returns 復元した email。email パラメータが無ければ空文字。
 */
export const getEmailFromResetLink = (search: string): string => {
  const query = search.startsWith('?') ? search.slice(1) : search;
  for (const pair of query.split('&')) {
    if (pair === '') continue;
    const eqIndex = pair.indexOf('=');
    const key = eqIndex === -1 ? pair : pair.slice(0, eqIndex);
    if (key !== 'email') continue;
    const rawValue = eqIndex === -1 ? '' : pair.slice(eqIndex + 1);
    try {
      // decodeURIComponent は '+' をそのまま残す(空白化しない)。
      return decodeURIComponent(rawValue);
    } catch {
      // 壊れた % シーケンス等でも空にせず素の値で返す。
      return rawValue;
    }
  }
  return '';
};

export const getViewerUrl = (
  tenantId: string,
  cameraId: string,
  tenantName?: string | null,
  jizaipadDomain?: string | null,
  viewerOrigin?: string | null,
  options?: {
    playbackAt?: string | null
    playbackMode?: 'live' | 'recording' | null
  }
): string => {
  let origin: string;

  if (viewerOrigin != null && viewerOrigin.trim() !== '') {
    origin = viewerOrigin;
  } else if (tenantName != null && tenantName.trim() !== '' && jizaipadDomain != null && jizaipadDomain.trim() !== '') {
    origin = `https://${tenantName}.${jizaipadDomain}`;
  } else {
    origin = `https://${new URL(window.location.href).hostname}`;
  }

  const url = new URL('/viewer/', origin)
  url.searchParams.set('tenantId', tenantId)
  url.searchParams.set('cameraId', cameraId)

  const playbackAt = options?.playbackAt?.trim()
  if (playbackAt != null && playbackAt !== '') {
    url.searchParams.set('playbackAt', playbackAt)
  }

  const playbackMode = options?.playbackMode?.trim()
  if (playbackMode === 'live' || playbackMode === 'recording') {
    url.searchParams.set('playbackMode', playbackMode)
  }

  return url.toString()
};

const getBaseDomain = (hostname: string): string => {
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return parts.slice(-2).join('.');
  }
  return hostname;
};

const normalizePath = (path: string): string => {
  let processedPath = path.startsWith('/') ? path : `/${path}`;
  if (processedPath === '/') {
    return '';
  }
  if (processedPath.endsWith('/')) {
    return processedPath.slice(0, -1);
  }
  return processedPath;
};

const shouldForceDevDomain = (hostname: string): boolean => {
  return getBaseDomain(hostname) === 'jizaie.net';
};

export const getRedirectUrl = (
  path: string = '/',
  tenantName?: string | null,
  jizaipadDomain?: string | null
): string => {
  const currentHost = new URL(window.location.href).hostname;
  const processedPath = normalizePath(path);
  const trimmedTenant = tenantName?.trim();
  const trimmedDomain = jizaipadDomain?.trim();
  const hasTenant = typeof trimmedTenant === 'string' && trimmedTenant.length > 0;
  const hasDomain = typeof trimmedDomain === 'string' && trimmedDomain.length > 0;
  const currentBaseDomain = getBaseDomain(currentHost);

  // tenant.jizaipadDomain 形式で URL を生成
  if (hasTenant && hasDomain && trimmedTenant !== undefined && trimmedDomain !== undefined) {
    if (shouldForceDevDomain(currentHost)) {
      // common_server は dev-* を individual dev として配信するため、認証後も現在の配信先を維持する
      if (trimmedTenant === 'jizaie' || currentHost.startsWith('dev-')) {
        return `https://${currentHost}${processedPath}`;
      }
      return `https://${trimmedTenant}.${currentBaseDomain}${processedPath}`;
    }
    return `https://${trimmedTenant}.${trimmedDomain}${processedPath}`;
  }

  // フォールバック
  return `https://${currentHost}${processedPath}`;
};
